import type { ExternalIds, TrackingPrivacyMode } from '../contracts';
import type { TrackerLogger } from '../logger';

/** The ingestion contract bounds each id; longer values are cut, not dropped. */
const MAX_LENGTH: Record<keyof ExternalIds, number> = {
  hubspotutk: 200,
  ga4ClientId: 200,
  fbp: 200,
  fbc: 500
};

const readCookie = (cookie: string, name: string): string | null => {
  for (const part of cookie.split(';')) {
    const separator = part.indexOf('=');
    if (separator === -1 || part.slice(0, separator).trim() !== name) continue;
    const value = part.slice(separator + 1).trim();
    if (!value) return null;
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  return null;
};

/** "GA1.1.2031493897.1788561739" carries the client id in its last two fields. */
const ga4ClientIdFrom = (value: string): string | null => {
  const parts = value.split('.');
  if (parts.length < 4) return null;
  const clientId = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  return /^\d+\.\d+$/.test(clientId) ? clientId : null;
};

/**
 * The ids other tools keep in the site's own cookies, so the visitor can be
 * matched to the same person in HubSpot, GA4 and Meta. Null when none is set.
 */
export const collectExternalIds = (cookie: string): ExternalIds | null => {
  const ids: ExternalIds = {};
  const hubspotutk = readCookie(cookie, 'hubspotutk');
  if (hubspotutk) ids.hubspotutk = hubspotutk.slice(0, MAX_LENGTH.hubspotutk);
  const ga = readCookie(cookie, '_ga');
  const ga4ClientId = ga ? ga4ClientIdFrom(ga) : null;
  if (ga4ClientId) ids.ga4ClientId = ga4ClientId.slice(0, MAX_LENGTH.ga4ClientId);
  const fbp = readCookie(cookie, '_fbp');
  if (fbp) ids.fbp = fbp.slice(0, MAX_LENGTH.fbp);
  const fbc = readCookie(cookie, '_fbc');
  if (fbc) ids.fbc = fbc.slice(0, MAX_LENGTH.fbc);
  return Object.keys(ids).length > 0 ? ids : null;
};

/**
 * Read at every flush, because consent tools and HubSpot set their cookies
 * after the page loads. Anonymous mode never reads them.
 */
export const readExternalIds = (
  logger: TrackerLogger,
  privacyMode: TrackingPrivacyMode
): ExternalIds | null => {
  if (privacyMode === 'anonymous') return null;
  try {
    return collectExternalIds(document.cookie);
  } catch (error) {
    logger.debug('collect_external_ids', {
      outcome: 'unavailable',
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};
